import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  getStockProducts,
  createStockProduct,
  updateStockProduct,
  deleteStockProduct,
  updateStockQuantity
} from "../../services/stockLocalService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { FaPlus, FaEdit, FaTrash, FaEye, FaBoxes, FaSearch } from "react-icons/fa";
import "../../styles/pages/StockLocalPage.css";

function StockLocalPage() {
  const { canEditStock } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit' | 'view'
  const [searchFilter, setSearchFilter] = useState(''); // Filtro de búsqueda
  const [currentProduct, setCurrentProduct] = useState({
    product_name: '',
    product_description: '',
    unit_price: '',
    stock_quantity: 0
  });

  const isReadOnly = !canEditStock();

  // Filtrar productos por nombre
  const filteredProducts = products.filter(product => {
    if (!searchFilter.trim()) return true;
    const searchLower = searchFilter.toLowerCase();
    return product.product_name?.toLowerCase().includes(searchLower) ||
           product.product_description?.toLowerCase().includes(searchLower);
  });

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await getStockProducts(false);
      if (response.data && response.data.success) {
        setProducts(response.data.data || []);
      }
    } catch (error) {
      console.error('Error cargando productos:', error);
      toast.error('Error al cargar productos de stock');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (mode, product = null) => {
    setModalMode(mode);
    if (product) {
      setCurrentProduct(product);
    } else {
      setCurrentProduct({
        product_name: '',
        product_description: '',
        unit_price: '',
        stock_quantity: 0
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentProduct({
      product_name: '',
      product_description: '',
      unit_price: '',
      stock_quantity: 0
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentProduct(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!currentProduct.product_name.trim()) {
      toast.error('El nombre del producto es obligatorio');
      return;
    }
    
    if (!currentProduct.unit_price || parseFloat(currentProduct.unit_price) <= 0) {
      toast.error('El precio debe ser mayor a 0');
      return;
    }

    try {
      if (modalMode === 'create') {
        const response = await createStockProduct(currentProduct);
        if (response.data && response.data.success) {
          toast.success('Producto creado exitosamente');
          loadProducts();
          handleCloseModal();
        } else {
          toast.error(response.data?.message || 'Error al crear producto');
        }
      } else if (modalMode === 'edit') {
        const response = await updateStockProduct(currentProduct.id, currentProduct);
        if (response.data && response.data.success) {
          toast.success('Producto actualizado exitosamente');
          loadProducts();
          handleCloseModal();
        } else {
          toast.error(response.data?.message || 'Error al actualizar producto');
        }
      }
    } catch (error) {
      console.error('Error en handleSubmit:', error);
      toast.error('Error al procesar la solicitud');
    }
  };

  const handleDelete = async (id, productName) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      html: `Se desactivará el producto <strong>${productName}</strong>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, desactivar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteStockProduct(id);
        if (response.data && response.data.success) {
          toast.success('Producto desactivado exitosamente');
          loadProducts();
        } else {
          toast.error(response.data?.message || 'Error al desactivar producto');
        }
      } catch (error) {
        console.error('Error eliminando producto:', error);
        toast.error('Error al desactivar producto');
      }
    }
  };

  const handleQuickUpdateQuantity = async (id, currentQuantity, productName) => {
    const { value: newQuantity } = await Swal.fire({
      title: `Actualizar cantidad: ${productName}`,
      input: 'number',
      inputLabel: 'Nueva cantidad en stock',
      inputValue: currentQuantity,
      inputAttributes: {
        min: 0,
        step: 1
      },
      showCancelButton: true,
      confirmButtonText: 'Actualizar',
      cancelButtonText: 'Cancelar',
      inputValidator: (value) => {
        if (!value || parseInt(value) < 0) {
          return 'La cantidad debe ser 0 o mayor';
        }
      }
    });

    if (newQuantity !== undefined) {
      try {
        const response = await updateStockQuantity(id, parseInt(newQuantity));
        if (response.data && response.data.success) {
          toast.success('Cantidad actualizada exitosamente');
          loadProducts();
        } else {
          toast.error(response.data?.message || 'Error al actualizar cantidad');
        }
      } catch (error) {
        console.error('Error actualizando cantidad:', error);
        toast.error('Error al actualizar cantidad');
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="stock-local-page container-fluid p-4">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">
                <FaBoxes className="me-2" />
                Stock Local
              </h2>
              <p className="text-muted mb-0">
                {isReadOnly 
                  ? 'Consulta de productos en stock (solo lectura)'
                  : 'Gestión de productos y accesorios en tienda física'
                }
              </p>
            </div>
            {!isReadOnly && (
              <button
                className="btn btn-primary"
                onClick={() => handleOpenModal('create')}
              >
                <FaPlus className="me-2" />
                Nuevo Producto
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabla de productos */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              {/* Campo de búsqueda */}
              <div className="row mb-3">
                <div className="col-md-4">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">
                      <FaSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar por nombre o descripción..."
                      value={searchFilter}
                      onChange={(e) => setSearchFilter(e.target.value)}
                    />
                  </div>
                  <small className="text-muted">Busca en tiempo real</small>
                </div>
                {searchFilter && (
                  <div className="col-md-8">
                    <div className="alert alert-info py-2 mb-0">
                      <small>
                        <FaSearch className="me-1" />
                        Mostrando {filteredProducts.length} de {products.length} productos
                      </small>
                    </div>
                  </div>
                )}
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <p className="mt-2 text-muted">Cargando productos...</p>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-5">
                  <FaBoxes size={48} className="text-muted mb-3" />
                  {searchFilter ? (
                    <p className="text-muted">No se encontraron productos que coincidan con la búsqueda</p>
                  ) : (
                    <>
                      <p className="text-muted">No hay productos de stock registrados</p>
                      {!isReadOnly && (
                        <button
                          className="btn btn-primary mt-2"
                          onClick={() => handleOpenModal('create')}
                        >
                          <FaPlus className="me-2" />
                          Agregar primer producto
                        </button>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '5%' }}>#</th>
                        <th style={{ width: '25%' }}>Producto</th>
                        <th style={{ width: '30%' }}>Descripción</th>
                        <th style={{ width: '12%' }} className="text-end">Precio Unit.</th>
                        <th style={{ width: '10%' }} className="text-center">Stock</th>
                        <th style={{ width: '18%' }} className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product, index) => (
                        <tr key={product.id}>
                          <td className="align-middle">{index + 1}</td>
                          <td className="align-middle">
                            <strong>{product.product_name}</strong>
                          </td>
                          <td className="align-middle">
                            <small className="text-muted">
                              {product.product_description || '-'}
                            </small>
                          </td>
                          <td className="align-middle text-end">
                            <strong className="text-success">
                              {formatCurrency(product.unit_price)}
                            </strong>
                          </td>
                          <td className="align-middle text-center">
                            <span 
                              className={`badge ${
                                product.stock_quantity === 0 
                                  ? 'bg-danger' 
                                  : product.stock_quantity <= 5 
                                  ? 'bg-warning' 
                                  : 'bg-success'
                              }`}
                              style={{ cursor: !isReadOnly ? 'pointer' : 'default', fontSize: '0.9rem' }}
                              onClick={() => !isReadOnly && handleQuickUpdateQuantity(
                                product.id, 
                                product.stock_quantity,
                                product.product_name
                              )}
                              title={!isReadOnly ? 'Click para actualizar cantidad' : ''}
                            >
                              {product.stock_quantity} unid.
                            </span>
                          </td>
                          <td className="align-middle text-center">
                            <div className="btn-group btn-group-sm" role="group">
                              <button
                                className="btn btn-outline-info"
                                onClick={() => handleOpenModal('view', product)}
                                title="Ver detalles"
                              >
                                <FaEye />
                              </button>
                              {!isReadOnly && (
                                <>
                                  <button
                                    className="btn btn-outline-primary"
                                    onClick={() => handleOpenModal('edit', product)}
                                    title="Editar"
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    className="btn btn-outline-danger"
                                    onClick={() => handleDelete(product.id, product.product_name)}
                                    title="Desactivar"
                                  >
                                    <FaTrash />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal para crear/editar/ver producto */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalMode === 'create' && 'Nuevo Producto'}
                  {modalMode === 'edit' && 'Editar Producto'}
                  {modalMode === 'view' && 'Detalle del Producto'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                  aria-label="Close"
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="product_name" className="form-label">
                      Nombre del Producto <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="product_name"
                      name="product_name"
                      value={currentProduct.product_name}
                      onChange={handleInputChange}
                      disabled={modalMode === 'view'}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="product_description" className="form-label">
                      Descripción
                    </label>
                    <textarea
                      className="form-control"
                      id="product_description"
                      name="product_description"
                      rows="3"
                      value={currentProduct.product_description}
                      onChange={handleInputChange}
                      disabled={modalMode === 'view'}
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="unit_price" className="form-label">
                        Precio Unitario <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          type="number"
                          className="form-control"
                          id="unit_price"
                          name="unit_price"
                          value={currentProduct.unit_price}
                          onChange={handleInputChange}
                          disabled={modalMode === 'view'}
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="stock_quantity" className="form-label">
                        Cantidad en Stock
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="stock_quantity"
                        name="stock_quantity"
                        value={currentProduct.stock_quantity}
                        onChange={handleInputChange}
                        disabled={modalMode === 'view'}
                        min="0"
                        step="1"
                      />
                    </div>
                  </div>

                  {modalMode === 'view' && currentProduct.inserted_by_name && (
                    <div className="alert alert-info mb-0">
                      <small>
                        <strong>Creado por:</strong> {currentProduct.inserted_by_name}
                        <br />
                        <strong>Fecha:</strong> {new Date(currentProduct.inserted_date).toLocaleString('es-AR')}
                      </small>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                  >
                    {modalMode === 'view' ? 'Cerrar' : 'Cancelar'}
                  </button>
                  {modalMode !== 'view' && (
                    <button type="submit" className="btn btn-primary">
                      {modalMode === 'create' ? 'Crear Producto' : 'Guardar Cambios'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StockLocalPage;
